import type React from "react"
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer"

// Register a font if needed, otherwise it defaults to Helvetica
// Font.register({
//   family: 'Roboto',
//   src: 'https://fonts.gstatic.com/s/roboto/v27/KFOmCnqEu92Fr1Mu4mxK.ttf'
// });

// Create styles for the PDF document
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 30,
    fontSize: 9,
    fontFamily: "Helvetica", // Default font
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
  header: {
    textAlign: "center",
    marginBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: "#2196F3", // Blue-600
    paddingBottom: 10,
  },
  companyName: {
    fontSize: 18,
    fontWeight: "bold", // Ensure bold
    color: "#212121", // Gray-900
  },
  companySlogan: {
    fontSize: 10,
    color: "#616161", // Gray-700
  },
  statementTitle: {
    fontSize: 22,
    fontWeight: "bold", // Ensure bold
    color: "#212121", // Gray-900
    marginTop: 10,
  },
  statementPeriod: {
    fontSize: 12,
    color: "#616161", // Gray-700
  },
  infoCardsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 15,
  },
  infoCard: {
    width: "32%", // Approx 1/3 width for each card
    borderWidth: 1,
    borderColor: "#e0e0e0", // Gray-300
    borderRadius: 4,
    padding: 8,
    backgroundColor: "#f5f5f5", // Light gray background for cards
  },
  cardTitle: {
    fontSize: 11, // Slightly increased font size for visibility
    fontWeight: "bold", // Ensure bold
    textAlign: "center",
    marginBottom: 5,
    paddingVertical: 3,
    borderRadius: 2,
  },
  cardRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottomWidth: 1,
    borderBottomColor: "#eeeeee", // Gray-200
    paddingVertical: 2,
  },
  cardLabel: {
    fontSize: 8,
    color: "#424242", // Gray-800
    fontWeight: "medium",
  },
  cardValue: {
    fontSize: 8,
    color: "#212121", // Gray-900
    fontWeight: "bold",
  },
  cardAmountValue: {
    fontSize: 8,
    color: "#01579b", // Blue-700
    fontWeight: "bold",
  },
  tableContainer: {
    marginBottom: 15,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 4,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6", // Gray-100
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eeeeee", // Gray-200
  },
  tableCellHeader: {
    padding: 4,
    fontWeight: "bold",
    fontSize: 7,
    flex: 1,
    textAlign: "left",
  },
  tableCell: {
    padding: 4,
    fontSize: 7,
    flex: 1,
    textAlign: "left",
  },
  tableCellRight: {
    textAlign: "right",
  },
  tableCellDescription: {
    flex: 2, // Make description column wider
  },
  tableGrandTotalRow: {
    flexDirection: "row",
    backgroundColor: "#e0f2f7", // Blue-100
    borderTopWidth: 1,
    borderTopColor: "#2196F3",
  },
  tableGrandTotalCell: {
    padding: 4,
    fontWeight: "bold",
    fontSize: 8,
    color: "#004d40", // Blue-800
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#bdbdbd", // Gray-400
    paddingTop: 8,
    marginTop: 10,
  },
  footerText: {
    fontSize: 7,
    color: "#616161", // Gray-700
  },
  disclaimer: {
    marginTop: 8,
    padding: 6,
    backgroundColor: "#f9fafb", // Gray-50
    borderLeftWidth: 4,
    borderLeftColor: "#2196F3", // Blue-600
    borderRadius: 4,
  },
  disclaimerText: {
    fontSize: 7,
    color: "#616161", // Gray-700
  },
})

interface Transaction {
  txnDate: string
  voucher: string
  narration: string
  debit: number
  credit: number
  balance: number
}

interface ROIHistory {
  sno: number
  startDate: string
  endDate: string
  roi: string
  emiAmount: number
}

interface LoanStatementData {
  borrowerName: string
  coApplicantName: string
  address: string
  loanNumber: string
  disbursedAmount: number
  termOfLoan: number
  termUnit: string
  rateOfInterest: number
  emiStartDate: string
  periodFrom: string
  periodTo: string
  transactions: Transaction[]
  roiHistory: ROIHistory[]
  pageInfo: {
    currentPage: number
    totalPages: number
    takenBy: string
    dateTime: string
  }
}

interface PdfStatementProps {
  data: LoanStatementData
}

const PdfStatement: React.FC<PdfStatementProps> = ({ data }) => {
  const formatIndianNumber = (num: number): string => {
    if (num === 0) return "0.00"
    const isNegative = num < 0
    const absNum = Math.abs(num)
    const formatted = absNum.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    return isNegative ? `-${formatted}` : formatted
  }

  // Exclude accrual transactions for the statement PDF
  const filteredTransactions = data.transactions.filter((t) => !/accrual/i.test(t.narration || ''))
  // Limit transactions for PDF to fit on one page, e.g., 15-20 rows
  const transactionsForPdf = filteredTransactions.slice(0, 18) // Adjust this number as needed

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={{ flexDirection: "column", justifyContent: "center", alignItems: "center", marginBottom: 8 }}>
            {/* Logo centered and larger */}
            <Image
              src="/logo.png"
              style={{
                height: 60,
                marginBottom: 4,
              }}
            />
          </View>
          <Text style={styles.statementTitle}>LOAN STATEMENT</Text>
          <Text style={styles.statementPeriod}>
            Period: {data.periodFrom} to {data.periodTo}
          </Text>
        </View>

        {/* Info Cards */}
        <View style={styles.infoCardsContainer}>
          {/* Borrower Information */}
          <View style={[styles.infoCard, { backgroundColor: "#e0f2f7" }]}>
            <Text style={[styles.cardTitle, { backgroundColor: "#b3e5fc" }]}>BORROWER INFORMATION</Text>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Name</Text>
              <Text style={styles.cardValue}>{`Mr/Mrs. ${data.borrowerName}`}</Text>
            </View>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Co-Applicant</Text>
              <Text style={styles.cardValue}>{`Mr/Mrs. ${data.coApplicantName || "N/A"}`}</Text>
            </View>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Address</Text>
              <Text style={styles.cardValue}>{data.address}</Text>
            </View>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Loan Number</Text>
              <Text style={styles.cardValue}>{data.loanNumber}</Text>
            </View>

          </View>

          {/* Loan Details */}
          <View style={[styles.infoCard, { backgroundColor: "#e8f5e9" }]}>
            <Text style={[styles.cardTitle, { backgroundColor: "#c8e6c9" }]}>LOAN DETAILS</Text>

            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Disbursed Amount</Text>
              <Text style={styles.cardAmountValue}>${formatIndianNumber(data.disbursedAmount)}</Text>
            </View>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Term of Loan</Text>
              <Text style={styles.cardValue}>{`${data.termOfLoan} ${data.termUnit}`}</Text>
            </View>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Rate of Interest</Text>
              <Text style={styles.cardValue}>{`${data.rateOfInterest}% Per Annum`}</Text>
            </View>

          </View>


        </View>

        {/* Transaction History */}
        <View style={[styles.tableContainer, { backgroundColor: "#f9fafb" }]}>
          <Text style={[styles.cardTitle, { backgroundColor: "#f3f4f6", marginBottom: 5 }]}>TRANSACTION HISTORY</Text>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCellHeader, { flex: 0.8 }]}>Date</Text>
            <Text style={[styles.tableCellHeader, { flex: 0.8 }]}>Reference</Text>
            <Text style={[styles.tableCellHeader, styles.tableCellDescription]}>Description</Text>
            <Text style={[styles.tableCellHeader, styles.tableCellRight, { flex: 0.8 }]}>Debit</Text>
            <Text style={[styles.tableCellHeader, styles.tableCellRight, { flex: 0.8 }]}>Credit</Text>
            <Text style={[styles.tableCellHeader, styles.tableCellRight, { flex: 0.8 }]}>Balance</Text>
          </View>
          {transactionsForPdf.map((transaction, index) => (
            <View key={index} style={[styles.tableRow, { backgroundColor: index % 2 === 0 ? "#fcfcfc" : "#ffffff" }]}>
              <Text style={[styles.tableCell, { flex: 0.8 }]}>{transaction.txnDate}</Text>
              <Text style={[styles.tableCell, { flex: 0.8 }]}>{transaction.voucher}</Text>
              <Text style={[styles.tableCell, styles.tableCellDescription]}>{transaction.narration}</Text>
              <Text style={[styles.tableCell, styles.tableCellRight, { flex: 0.8 }]}>
                {transaction.debit > 0 ? formatIndianNumber(transaction.debit) : "-"}
              </Text>
              <Text style={[styles.tableCell, styles.tableCellRight, { flex: 0.8 }]}>
                {transaction.credit > 0 ? formatIndianNumber(transaction.credit) : "-"}
              </Text>
              <Text style={[styles.tableCell, styles.tableCellRight, { flex: 0.8, fontWeight: "bold" }]}>
                {formatIndianNumber(transaction.balance)}
              </Text>
            </View>
          ))}

          {filteredTransactions.length > transactionsForPdf.length && (
            <Text style={{ textAlign: "center", fontSize: 7, color: "#616161", marginTop: 5 }}>
              Showing first {transactionsForPdf.length} transactions (excluding accruals). Full list available in Excel export.
            </Text>
          )}
        </View>

        {/* ROI History */}
        <View style={[styles.tableContainer, { backgroundColor: "#faf5ff" }]}>
          <Text style={[styles.cardTitle, { backgroundColor: "#f3e5f5", marginBottom: 5 }]}>
            RATE OF INTEREST HISTORY
          </Text>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCellHeader, { flex: 0.5 }]}>S.No</Text>
            <Text style={[styles.tableCellHeader, { flex: 1 }]}>Start Date</Text>
            <Text style={[styles.tableCellHeader, { flex: 1 }]}>End Date</Text>
            <Text style={[styles.tableCellHeader, { flex: 0.8 }]}>ROI</Text>
            <Text style={[styles.tableCellHeader, styles.tableCellRight, { flex: 1 }]}>EMI Amount</Text>
          </View>
          {data.roiHistory.map((roi, index) => (
            <View key={index} style={[styles.tableRow, { backgroundColor: index % 2 === 0 ? "#fcfcfc" : "#ffffff" }]}>
              <Text style={[styles.tableCell, { flex: 0.5 }]}>{roi.sno}</Text>
              <Text style={[styles.tableCell, { flex: 1 }]}>{roi.startDate}</Text>
              <Text style={[styles.tableCell, { flex: 1 }]}>{roi.endDate}</Text>
              <Text style={[styles.tableCell, { flex: 0.8 }]}>{roi.roi}</Text>
              <Text style={[styles.tableCell, styles.tableCellRight, { flex: 1 }]}>
                {formatIndianNumber(roi.emiAmount)}
              </Text>
            </View>
          ))}
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
          <View>
            <Text style={styles.footerText}>
              Total Pages: {data.pageInfo.totalPages} | Generated By: {data.pageInfo.takenBy}
            </Text>
            <Text style={styles.footerText}>Date & Time: {data.pageInfo.dateTime}</Text>
          </View>
          <View style={{ textAlign: "right" }}>
            <Text style={styles.footerText}>
              Page {data.pageInfo.currentPage} of {data.pageInfo.totalPages}
            </Text>
            <Text style={{ fontSize: 10, fontWeight: "bold", color: "#212121", marginTop: 4 }}>
              *** END OF STATEMENT ***
            </Text>
          </View>
        </View>

        {/* Disclaimer */}
        <View style={styles.disclaimer} fixed>
          <Text style={styles.disclaimerText}>
            Note: This is a computer-generated statement. For discrepancies, contact customer service within 30 days.
          </Text>
        </View>
      </Page>
    </Document>
  )
}

export default PdfStatement
